import React from "react";
import { getClassName } from "@utils";
import { WarningIcon } from "@taskpane/icons";
import { Avatar, AvatarProps, AvatarSize } from "../Avatar";
import { Tooltip, TooltipSize } from "../Tooltip";
import { AnalysisItem } from "../AnalysisItem";
import { Skeleton, SkeletonSize } from "../Skeleton";
import css from "./UpperHandAnalysis.module.scss";

export interface UpperHandAnalysisParticipant {
  avatarProps: AvatarProps;
  name: string;
  email: string;
  position?: string;
}

export interface UpperHandAnalysisProps {
  dominantSide?: "left" | "right" | "unresolved";
  isLoading?: boolean;
  leftParticipant?: UpperHandAnalysisParticipant;
  rightParticipant?: UpperHandAnalysisParticipant;
}

const UpperHandAnalysis: React.FC<UpperHandAnalysisProps> = ({
  dominantSide = "unresolved",
  isLoading = false,
  leftParticipant,
  rightParticipant,
}) => {
  const renderTooltipContent = (participant?: UpperHandAnalysisParticipant) => {
    const attrs = {
      avatarTooltipContent: {
        className: css.avatarTooltipContent,
      },
      avatarTooltipName: {
        className: css.avatarTooltipName,
      },
      avatarTooltipPosition: {
        className: css.avatarTooltipPosition,
      },
      avatarTooltipEmail: {
        className: css.avatarTooltipEmail,
      },
    };

    return (
      <div {...attrs.avatarTooltipContent}>
        <span {...attrs.avatarTooltipName}>{participant?.name}</span>
        {participant?.position ? (
          <span {...attrs.avatarTooltipPosition}>{participant.position}</span>
        ) : null}
        <span {...attrs.avatarTooltipEmail}>{participant?.email}</span>
      </div>
    );
  };

  const attrs = {
    analysisItem: {
      isLoading,
      title: "Upper hand",
      tooltip: "View the power-balance between you and your prospect at any given point.",
    },
    container: {
      className: css.container,
    },
    leftParticipantName: {
      className: getClassName(css.text, css.textLeftParticipant),
    },
    avatarsWrapper: {
      className: css.avatarsWrapper,
    },
    rightParticipantName: {
      className: getClassName(css.text, css.textRightParticipant),
    },
    leftParticipantTooltip: {
      className: css.avatarTooltip,
      size: TooltipSize.XS,
      arrow: false,
      autoPosition: true,
      content: renderTooltipContent(leftParticipant),
    },
    rightParticipantTooltip: {
      className: css.avatarTooltip,
      size: TooltipSize.XS,
      arrow: false,
      autoPosition: true,
      content: renderTooltipContent(rightParticipant),
    },
    leftParticipantAvatar: {
      isLoading,
      className: css.avatar,
      size: !isLoading && dominantSide === "left" ? AvatarSize.XL : AvatarSize.M,
      ...leftParticipant?.avatarProps,
    },
    rightParticipantAvatar: {
      isLoading,
      className: css.avatar,
      size: !isLoading && dominantSide === "right" ? AvatarSize.XL : AvatarSize.M,
      ...rightParticipant?.avatarProps,
    },
    separator: {
      className: css.separator,
    },
    warningTooltip: {
      className: css.warningTooltip,
      contentClassName: css.warningTooltipContent,
      size: TooltipSize.XS,
      arrow: false,
      text: "Upper hand cannot be determined yet",
    },
    warningIcon: {
      className: css.warningIcon,
    },
    nameSkeleton: {
      rows: 2,
      width: 35,
      size: SkeletonSize.S,
      className: css.nameSkeleton,
    },
  };

  if (isLoading) {
    return (
      <AnalysisItem {...attrs.analysisItem}>
        <div {...attrs.container}>
          <div {...attrs.leftParticipantName}>
            <Skeleton {...attrs.nameSkeleton} />
          </div>
          <div {...attrs.avatarsWrapper}>
            <div>
              <Avatar {...attrs.leftParticipantAvatar} />
            </div>
            <div>
              <Avatar {...attrs.rightParticipantAvatar} />
            </div>
            <div {...attrs.separator} />
          </div>
          <div {...attrs.rightParticipantName}>
            <Skeleton {...attrs.nameSkeleton} />
          </div>
        </div>
      </AnalysisItem>
    );
  }

  return (
    <AnalysisItem {...attrs.analysisItem}>
      <div {...attrs.container}>
        <div {...attrs.leftParticipantName}>
          <span>{leftParticipant?.name || ""}</span>
        </div>
        <div {...attrs.avatarsWrapper}>
          <div>
            <Tooltip {...attrs.leftParticipantTooltip}>
              <Avatar {...attrs.leftParticipantAvatar} />
            </Tooltip>
          </div>
          <div>
            <Tooltip {...attrs.rightParticipantTooltip}>
              <Avatar {...attrs.rightParticipantAvatar} />
            </Tooltip>
          </div>
          {dominantSide === "unresolved" ? (
            <Tooltip {...attrs.warningTooltip}>
              <WarningIcon {...attrs.warningIcon} />
            </Tooltip>
          ) : (
            <div {...attrs.separator} />
          )}
        </div>
        <div {...attrs.rightParticipantName}>
          <span>{rightParticipant?.name || ""}</span>
        </div>
      </div>
    </AnalysisItem>
  );
};

export default UpperHandAnalysis;
